"use client";

import { useState, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import { ScheduledCourse } from '@/app/types/course';
import { RutgersCourse } from '@/app/types/RutgersCourse';
import { isSemesterId } from '@/app/lib/semesters';
import { useCoursePeek } from '@/app/Components/CoursePeek/CoursePeekProvider';
import { scheduledSectionContext } from '@/app/Components/CoursePeek/contexts';

interface ContextMenuState {
  isOpen: boolean;
  x: number;
  y: number;
  course: ScheduledCourse | RutgersCourse | null;
}

interface UseCourseContextMenuReturn {
  contextMenu: ContextMenuState;
  handleContextMenu: (e: React.MouseEvent, course: ScheduledCourse | RutgersCourse) => void;
  closeContextMenu: () => void;
  handleAddToSniper: (course: ScheduledCourse | RutgersCourse) => void;
  handleViewInExplore: (course: ScheduledCourse | RutgersCourse) => void;
  handleCopyCourseInfo: (course: ScheduledCourse | RutgersCourse) => void;
  handleShareCourse: (course: ScheduledCourse | RutgersCourse) => void;
}

export function useCourseContextMenu(semester?: string): UseCourseContextMenuReturn {
  const displayedSemester = isSemesterId(semester) ? semester : undefined;
  const token = useMemo(() => ({ semester: displayedSemester }), [displayedSemester]);
  const peek = useCoursePeek();
  const committed = useRef<typeof token | null>(null);
  useLayoutEffect(() => {
    committed.current = token;
    return () => {
      if (committed.current === token) committed.current = null;
    };
  }, [token]);
  const [contextMenu, setContextMenu] = useState<ContextMenuState & { token?: typeof token }>({
    isOpen: false,
    x: 0,
    y: 0,
    course: null
  });

  const handleContextMenu = useCallback((e: React.MouseEvent, course: ScheduledCourse | RutgersCourse) => {
    if (committed.current !== token) return;
    e.preventDefault();
    const trigger = e.currentTarget as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    const openedFromKeyboard = e.clientX === 0 && e.clientY === 0;
    setContextMenu({
      isOpen: true,
      x: openedFromKeyboard ? rect.left + rect.width / 2 : e.clientX,
      y: openedFromKeyboard ? rect.top : e.clientY,
      course,
      token
    });
  }, [token]);

  const closeContextMenu = useCallback(() => {
    if (committed.current !== token) return;
    setContextMenu({
      isOpen: false,
      x: 0,
      y: 0,
      course: null
    });
  }, [token]);

  const handleAddToSniper = useCallback((course: ScheduledCourse | RutgersCourse) => {
    if (committed.current !== token) return;
    // Navigate to course sniper with the course pre-filled
    // Use the section index (id) for course sniper, not the baseId
    const sectionIndex = course.id;
    window.open(`/scheduling/alerts?course=${sectionIndex}${token.semester ? `&semester=${token.semester}` : ''}`, '_blank');
  }, [token]);

  const handleViewInExplore = useCallback((course: ScheduledCourse | RutgersCourse) => {
    if (committed.current !== token) return;
    // Keep the course paired with the term that supplied this callback.
    const baseId = course.baseId || `${course.department}:${course.class_number}`;
    if (peek) {
      // Stay on the schedule: open the shared course view with this section first.
      setContextMenu({ isOpen: false, x: 0, y: 0, course: null });
      peek.openCourse({ courseId: baseId, semester: token.semester, context: 'name' in course ? scheduledSectionContext(course, 'On this schedule') : undefined });
      return;
    }
    window.open(`/explore?course=${baseId}${token.semester ? `&semester=${token.semester}` : ''}`, '_blank');
  }, [peek, token]);

  const handleCopyCourseInfo = useCallback(async (course: ScheduledCourse | RutgersCourse) => {
    let courseInfo = '';
    
    if ('name' in course) {
      // ScheduledCourse
      courseInfo = `${course.name} (${course.department} ${course.class_number} - Section ${course.section})
Campus: ${course.campus}
Credits: ${course.credits}
Instructors: ${course.instructors?.join(', ') || 'TBA'}
Location: ${course.location || 'TBA'}
Schedule: ${course.meetingTimes?.map(mt => `${mt.day} ${mt.startMinute ? `${Math.floor(mt.startMinute / 60)}:${String(mt.startMinute % 60).padStart(2, '0')}` : 'TBA'}`).join(', ') || 'TBA'}
Core Requirements: ${course.coreRequirements?.join(', ') || 'None'}`;
    } else {
      // RutgersCourse
      courseInfo = `${course.courseName} (${course.department} ${course.class_number} - Section ${course.section})
Campus: ${course.campus}
Credits: ${course.credits}
Instructors: ${course.instructors?.join(', ') || 'TBA'}
Location: ${course.meetingTimes?.[0]?.location || 'TBA'}
Schedule: ${course.meetingTimes?.map(mt => `${mt.day} ${mt.startMinute ? `${Math.floor(mt.startMinute / 60)}:${String(mt.startMinute % 60).padStart(2, '0')}` : 'TBA'}`).join(', ') || 'TBA'}
Core Requirements: ${course.coreRequirements?.join(', ') || 'None'}`;
    }

    try {
      await navigator.clipboard.writeText(courseInfo);
      // You can add toast notification here if needed
      console.log('Course information copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy course info:', error);
    }
  }, []);

  const handleShareCourse = useCallback((course: ScheduledCourse | RutgersCourse) => {
    const courseName = 'name' in course ? course.name : course.courseName;
    const department = course.department;
    const classNumber = course.class_number;
    
    const shareText = `Check out this course: ${courseName} (${department} ${classNumber})`;
    const baseId = 'baseId' in course ? course.baseId : `${department}:${classNumber}`;
    const shareUrl = `${window.location.origin}/explore?course=${baseId}`;
    
    if (navigator.share) {
      navigator.share({
        title: courseName,
        text: shareText,
        url: shareUrl
      }).then(() => {
        console.log('Course shared successfully!');
      }).catch(() => {
        console.error('Failed to share course');
      });
    } else {
      // Fallback to copying to clipboard
      navigator.clipboard.writeText(`${shareText}\n${shareUrl}`).then(() => {
        console.log('Course link copied to clipboard!');
      }).catch(() => {
        console.error('Failed to copy course link');
      });
    }
  }, []);

  return {
    contextMenu: contextMenu.token === token ? contextMenu : { ...contextMenu, isOpen: false, course: null },
    handleContextMenu,
    closeContextMenu,
    handleAddToSniper,
    handleViewInExplore,
    handleCopyCourseInfo,
    handleShareCourse
  };
}
